import React, { RefObject, useLayoutEffect } from 'react';
import { useSet } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';
import {
  IndentedTextContent,
  StickyTableData,
  TableRow as StyledTableRow,
} from '../../TableRow.styles';
import { Liquidity } from '../../../../../../FlowTable.meta';
import { Icon } from '../../../../../../../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../../../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { AssetTableRow } from '../AssetTableRow/AssetTableRow';
import { Value } from '../Value/Value';
import { GroupTotal } from '../GroupTotal/GroupTotal';
import { TruncatedText } from '../../../../../../../../../../../../../../CommonComponents/OPL/Text/TruncatedText';

interface Props {
  liquidityData: Required<Liquidity>;
  tableRef: RefObject<HTMLTableElement>;
  setTableHeight: (arg: number) => void;
  collapsableHeaderIds: string[];
  hasHeaderSelected(id: number): boolean;
  hasShowCollapsed(id: number): boolean;
}

export const LiquidityTableRow = ({
  liquidityData,
  tableRef,
  setTableHeight,
  collapsableHeaderIds,
  hasHeaderSelected,
  hasShowCollapsed,
}: Props) => {
  const [, { has: hasExpansion, toggle: toggleExpansion }] = useSet<string>(
    new Set([liquidityData.name]),
  );

  useLayoutEffect(() => {
    if (tableRef?.current) {
      const { height } = tableRef.current.getBoundingClientRect();
      setTableHeight(height);
    }
  });

  return (
    <>
      {Object.entries(liquidityData.assets).map(
        ([assetGroupName, assetGroupData], liquidityAssetsIndex) => {
          if (Object.values(assetGroupData.child).length === 0) {
            return null;
          }

          return (
            <React.Fragment key={assetGroupName}>
              {liquidityAssetsIndex === 0 && (
                <StyledTableRow>
                  <StickyTableData
                    columnPosition={0}
                    onClick={() => {
                      toggleExpansion(liquidityData.name);
                    }}
                    colSpan={2}
                  >
                    <IndentedTextContent indentation={1} isExpandable>
                      <AnimatePresence>
                        <Icon
                          as={motion.i}
                          key="arrow-icon"
                          fontSize="body"
                          mr="16"
                          color="neutral.500"
                          className="far fa-angle-down"
                          animate={hasExpansion(liquidityData.name) ? 'open' : 'closed'}
                          initial="closed"
                          variants={{
                            open: { rotate: 0 },
                            closed: { rotate: -90 },
                          }}
                        />
                      </AnimatePresence>

                      <TruncatedText fontWeight={600} textAlign="left">
                        {liquidityData.name}
                      </TruncatedText>
                    </IndentedTextContent>
                  </StickyTableData>
                </StyledTableRow>
              )}

              {hasExpansion(liquidityData.name) && (
                <>
                  <StyledTableRow>
                    <StickyTableData
                      columnPosition={0}
                      onClick={() => {
                        toggleExpansion(assetGroupName);
                      }}
                    >
                      <IndentedTextContent indentation={2} isExpandable>
                        <AnimatePresence>
                          <Icon
                            as={motion.i}
                            key="arrow-icon"
                            fontSize="body"
                            mr="16"
                            color="neutral.500"
                            className="far fa-angle-down"
                            animate={hasExpansion(assetGroupName) ? 'open' : 'closed'}
                            initial="closed"
                            variants={{
                              open: { rotate: 0 },
                              closed: { rotate: -90 },
                            }}
                          />
                        </AnimatePresence>

                        <TruncatedText fontWeight={600} textAlign="left">
                          {assetGroupName}
                        </TruncatedText>
                      </IndentedTextContent>
                    </StickyTableData>

                    {assetGroupData.totals.map(({ value, collapsed, headerId }, totalIndex) => {
                      if (hasHeaderSelected(headerId)) {
                        return null;
                      }

                      const isCollapsableHeader = collapsableHeaderIds.includes(
                        headerId.toString(),
                      );

                      const isCollapsed = hasShowCollapsed(headerId) ? !collapsed : collapsed;

                      if (isCollapsableHeader && isCollapsed) {
                        return null;
                      }

                      return (
                        <React.Fragment key={totalIndex}>
                          {totalIndex === 0 ? (
                            <StickyTableData columnPosition={1}>
                              <Text fontWeight={600} variant="tabular" textAlign="right">
                                <Value value={value} />
                              </Text>
                            </StickyTableData>
                          ) : (
                            <td>
                              <Text fontWeight={600} variant="tabular" textAlign="right">
                                <Value value={value} />
                              </Text>
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </StyledTableRow>

                  {hasExpansion(assetGroupName) && (
                    <AssetTableRow
                      assets={assetGroupData.child}
                      collapsableHeaderIds={collapsableHeaderIds}
                      hasShowCollapsed={hasShowCollapsed}
                      hasHeaderSelected={hasHeaderSelected}
                    />
                  )}
                </>
              )}
            </React.Fragment>
          );
        },
      )}

      <GroupTotal
        totals={liquidityData.totals}
        hasHeaderSelected={hasHeaderSelected}
        collapsableHeaderIds={collapsableHeaderIds}
        hasShowCollapsed={hasShowCollapsed}
        indentation={1}
      />
    </>
  );
};
